import { useState, useEffect, useRef } from 'react';
import {
  Send,
  Bot,
  User,
  Sparkles,
  TrendingUp,
  Users,
  Briefcase,
  ArrowLeft,
  Lightbulb,
  Wrench,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface SuggestedPrompt {
  icon: React.ReactNode;
  title: string;
  prompt: string;
  category: string;
}

export default function AIAssistant() {
  const navigate = useNavigate();
  const { theme } = useWorkdayClientTheme();
  const { user } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showUpdateNotice, setShowUpdateNotice] = useState(true);
  const [disclaimerExpanded, setDisclaimerExpanded] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const getUserDisplayName = () => {
    if (!user) return 'You';
    const meta = user.user_metadata;
    if (meta?.full_name) return meta.full_name;
    if (meta?.name) return meta.name;
    if (user.email) return user.email.split('@')[0];
    return 'You';
  };

  const formatMessageDateTime = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }) + ' at ' + date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const suggestedPrompts: SuggestedPrompt[] = [
    {
      icon: <Users className="w-5 h-5" />,
      title: 'Find Consultants',
      prompt: 'Help me find the best Workday HCM consultant for a 6-month project',
      category: 'Talent'
    },
    {
      icon: <TrendingUp className="w-5 h-5" />,
      title: 'Project Optimization',
      prompt: 'How can I optimize my current Workday implementation project timeline?',
      category: 'Strategy'
    },
    {
      icon: <Briefcase className="w-5 h-5" />,
      title: 'Budget Planning',
      prompt: 'What should I budget for a full Workday HCM implementation?',
      category: 'Finance'
    },
    {
      icon: <Lightbulb className="w-5 h-5" />,
      title: 'Best Practices',
      prompt: 'What are the best practices for onboarding Workday consultants?',
      category: 'Operations'
    }
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (messageText?: string) => {
    const textToSend = messageText || input;
    if (!textToSend.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    setTimeout(() => {
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: getAIResponse(textToSend),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const getAIResponse = (userInput: string): string => {
    const input = userInput.toLowerCase();

    if (input.includes('consultant') || input.includes('talent')) {
      return `Based on your requirements, I recommend looking for consultants with the following qualifications:

1. **Experience Level**: 5+ years of Workday experience
2. **Certifications**: Workday HCM Pro certified
3. **Availability**: Consultants available within the next 2-4 weeks

I found 3 highly qualified candidates in our talent pool who match these criteria. They have experience with similar projects and excellent client ratings. Would you like me to provide more details about specific consultants or help you create a job posting?`;
    }

    if (input.includes('budget') || input.includes('cost')) {
      return `For a full Workday HCM implementation, here's a typical budget breakdown:

**Implementation Costs:**
- Software licenses: $150,000 - $300,000/year
- Consultant fees: $200,000 - $500,000
- Training and change management: $50,000 - $100,000
- Integration and customization: $75,000 - $150,000

**Total Estimated Budget**: $475,000 - $1,050,000

**Key Factors Affecting Cost:**
- Company size and complexity
- Number of modules implemented
- Timeline and project scope
- Level of customization needed

Would you like me to help you create a more detailed budget estimate for your specific needs?`;
    }

    if (input.includes('timeline') || input.includes('optimize') || input.includes('project')) {
      return `To optimize your Workday implementation timeline, consider these strategies:

**Phase 1: Planning (4-6 weeks)**
- Define scope and requirements
- Assemble project team
- Establish governance structure

**Phase 2: Design (6-8 weeks)**
- Configure system based on requirements
- Design integrations and reports
- Plan data migration

**Phase 3: Build & Test (8-12 weeks)**
- Build configurations
- Conduct thorough testing
- Train super users

**Phase 4: Deploy (2-4 weeks)**
- Final user training
- Go-live preparation
- Post-launch support

**Optimization Tips:**
- Use experienced consultants to avoid common pitfalls
- Run parallel testing phases to save time
- Leverage Workday's best practice configurations
- Maintain clear communication across all teams

Would you like specific recommendations for your project phase?`;
    }

    if (input.includes('onboarding') || input.includes('best practice')) {
      return `Here are the best practices for onboarding Workday consultants:

**Before Day 1:**
- Provide project documentation and context
- Set up necessary system access and credentials
- Share organizational charts and key contacts
- Define clear roles and responsibilities

**First Week:**
- Conduct comprehensive orientation
- Review project timeline and milestones
- Introduce to stakeholders and team members
- Establish communication protocols

**Ongoing Success:**
- Schedule regular check-ins and status updates
- Provide clear expectations and deliverables
- Foster open communication channels
- Document decisions and changes

**Key Resources to Share:**
- Current Workday configuration details
- Business process documentation
- Integration specifications
- Security and compliance requirements

Would you like help creating an onboarding checklist or setting up a consultant orientation program?`;
    }

    return `I'm here to help you with:

- **Talent Matching**: Find the perfect Workday consultants for your projects
- **Project Planning**: Optimize timelines and resource allocation
- **Budget Analysis**: Get cost estimates and ROI projections
- **Best Practices**: Learn industry-standard approaches to Workday implementations
- **Risk Management**: Identify and mitigate potential project risks

What would you like to know more about?`;
  };

  const handlePromptClick = (prompt: string) => {
    handleSend(prompt);
  };

  return (
    <div className={`h-screen overflow-hidden ${theme === 'dark' ? 'bg-black text-white' : 'bg-white text-gray-900'}`}>
      {showUpdateNotice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className={`relative w-full max-w-md rounded-2xl shadow-2xl p-8 ${
            theme === 'dark' ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <button
              onClick={() => setShowUpdateNotice(false)}
              className={`absolute top-4 right-4 p-1.5 rounded-full transition-colors ${
                theme === 'dark' ? 'hover:bg-gray-800 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#40E0D0]/20 to-[#0066FF]/20 border-2 border-[#40E0D0]/40 flex items-center justify-center mb-5">
                <Wrench className="w-8 h-8 text-[#40E0D0]" />
              </div>
              <h2 className="text-xl font-bold mb-3">Page Update In Progress</h2>
              <p className={`text-sm leading-relaxed mb-6 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                This page is currently undergoing updates and will be made available once all updates are completed. Thank you for your patience.
              </p>
              <button
                onClick={() => setShowUpdateNotice(false)}
                className="px-6 py-2.5 bg-[#40E0D0] text-black rounded-lg font-semibold hover:bg-[#40E0D0]/90 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 h-full flex flex-col ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
        <button
          onClick={() => navigate('/workday-client/dashboard')}
          className="mb-6 flex items-center text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-14 h-14 rounded-full overflow-hidden flex items-center justify-center bg-black">
              <img src="/3.png" alt="AI Assistant" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">AI Assistant</h1>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Powered by advanced AI to help optimize your Workday projects
              </p>
            </div>
          </div>

          {/* Important Disclaimer */}
          <div className={`mt-6 rounded-xl border-2 overflow-hidden ${
            theme === 'dark'
              ? 'bg-amber-900/20 border-amber-600/50'
              : 'bg-amber-50 border-amber-300'
          }`}>
            <button
              onClick={() => setDisclaimerExpanded(prev => !prev)}
              className="w-full flex items-center justify-between px-6 py-4 text-left"
            >
              <div className="flex items-center gap-3">
                <div className={`flex-shrink-0 p-2 rounded-lg ${
                  theme === 'dark' ? 'bg-amber-600/20' : 'bg-amber-100'
                }`}>
                  <Lightbulb className={`w-5 h-5 ${
                    theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                  }`} />
                </div>
                <h3 className={`font-bold text-base ${
                  theme === 'dark' ? 'text-amber-400' : 'text-amber-900'
                }`}>
                  Important Notice
                </h3>
              </div>
              {disclaimerExpanded
                ? <ChevronUp className={`w-5 h-5 flex-shrink-0 ${theme === 'dark' ? 'text-amber-400' : 'text-amber-700'}`} />
                : <ChevronDown className={`w-5 h-5 flex-shrink-0 ${theme === 'dark' ? 'text-amber-400' : 'text-amber-700'}`} />
              }
            </button>
            {disclaimerExpanded && (
              <div className="px-6 pb-5">
                <p className={`text-sm leading-relaxed ${
                  theme === 'dark' ? 'text-amber-200' : 'text-amber-900'
                }`}>
                  Please note that the information provided by the AI Assistant is general guidance based on AI analysis. All recommendations should be validated against your specific requirments, Workday tenant configuration and business requirements. This AI Chat is designed to assist and save you time, but <strong>always verify information for accuracy and never share sensitive or confidential data in this chat.</strong>
                </p>
              </div>
            )}
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center pb-32">
            <div className="text-center mb-12">
              <Sparkles className="w-16 h-16 mx-auto mb-4 text-[#40E0D0]" />
              <h2 className="text-2xl font-bold mb-2">How can I help you today?</h2>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                Get AI-powered insights for your Workday projects
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-3xl">
              {suggestedPrompts.map((prompt, index) => {
                const isComingSoon = index === 0;
                return isComingSoon ? (
                  <div
                    key={index}
                    className={`p-4 rounded-lg text-left relative cursor-default opacity-75 ${
                      theme === 'dark'
                        ? 'bg-gray-900 border border-gray-800'
                        : 'bg-white border border-gray-200 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[#40E0D0]/10 text-[#40E0D0]">
                        {prompt.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-semibold">{prompt.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {prompt.category}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#40E0D0]/15 text-[#40E0D0] font-medium border border-[#40E0D0]/30">
                            Coming Soon
                          </span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                          {prompt.prompt}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    key={index}
                    onClick={() => handlePromptClick(prompt.prompt)}
                    className={`p-4 rounded-lg text-left transition-all hover:scale-105 ${
                      theme === 'dark'
                        ? 'bg-gray-900 hover:bg-gray-800 border border-gray-800'
                        : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[#40E0D0]/10 text-[#40E0D0]">
                        {prompt.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold">{prompt.title}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {prompt.category}
                          </span>
                        </div>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {prompt.prompt}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="w-full max-w-3xl mt-6 flex gap-3">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask me anything about your Workday projects..."
                className="flex-1 px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-gray-400 focus:border-transparent"
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
                className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
              >
                <Send className="w-5 h-5" />
                Send
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto mb-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-black flex items-center justify-center">
                      <img src="/3.png" alt="AI" className="w-full h-full object-contain" />
                    </div>
                  </div>
                )}
                <div
                  className={`max-w-2xl px-4 py-3 rounded-lg ${
                    message.role === 'user'
                      ? 'bg-gray-300 text-gray-900'
                      : theme === 'dark'
                      ? 'bg-gray-900 border border-gray-800'
                      : 'bg-white border border-gray-200'
                  }`}
                >
                  {message.role === 'user' && (
                    <p className="text-xs font-semibold text-gray-600 mb-1">
                      {getUserDisplayName()}
                    </p>
                  )}
                  <p className="whitespace-pre-wrap">{message.content}</p>
                  <p className={`text-xs mt-2 ${
                    message.role === 'user' ? 'text-gray-500' : theme === 'dark' ? 'text-gray-500' : 'text-gray-400'
                  }`}>
                    {message.role === 'user'
                      ? formatMessageDateTime(message.timestamp)
                      : message.timestamp.toLocaleTimeString()
                    }
                  </p>
                </div>
                {message.role === 'user' && (
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-black flex items-center justify-center">
                    <img src="/3.png" alt="AI" className="w-full h-full object-contain" />
                  </div>
                </div>
                <div className={`px-4 py-3 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'
                }`}>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-[#40E0D0] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-[#40E0D0] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-[#40E0D0] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}

        <div className="pt-4 pb-2">
          <div className="flex gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask me anything about your Workday projects..."
              className={`flex-1 px-4 py-3 rounded-lg border ${
                theme === 'dark'
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent`}
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || isTyping}
              className="px-6 py-3 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            >
              <Send className="w-5 h-5" />
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
